import { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';

interface DropdownItem {
  to: string;
  label: string;
}

interface Props {
  label: string;
  items: DropdownItem[];
  isOpen: boolean;
  onToggle: () => void;
  pathname: string;
}

export default function AdminNavDropdown({ label, items, isOpen, onToggle, pathname }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const isActive = items.some((item) => pathname === item.to || pathname.startsWith(item.to + '/'));

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onToggle();
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onToggle();
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onToggle]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={onToggle}
        className={`flex items-center gap-0.5 bg-transparent border-none cursor-pointer font-mono text-sm ${
          isActive ? 'text-accent font-bold' : 'text-muted hover:text-accent'
        }`}
      >
        {label}
        <ChevronDown
          size={12}
          className={`transition-transform duration-150 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen && (
        <div
          className="absolute top-full left-0 mt-1 min-w-[160px] py-1 z-40"
          style={{
            backgroundColor: 'var(--color-background)',
            border: '1px solid var(--color-border)',
          }}
        >
          {items.map((item) => {
            const itemActive = pathname === item.to || pathname.startsWith(item.to + '/');
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`block px-3 py-1.5 text-sm font-mono no-underline ${
                  itemActive ? 'text-accent font-bold' : 'text-muted hover:text-accent'
                }`}
                style={itemActive ? { backgroundColor: 'rgba(245,245,245,0.05)' } : undefined}
              >
                {item.label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
